import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
  Fade
} from '@mui/material';
import {
  Warning,
  Restore,
  AccessTime,
  Person,
  Code,
  Folder,
  ArrowBack
} from '@mui/icons-material';

export default function RollbackDialog({ open, onClose, onConfirm, deployment, loading }) {
  const theme = useTheme();

  if (!deployment) return null;

  const formatTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.dark, 0.1)} 100%)`,
        borderBottom: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
        py: 2.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
            boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}`
          }}>
            <Restore sx={{ color: 'white', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Confirm Rollback
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Revert to a previous deployment version
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Fade in timeout={300}>
          <Alert
            severity="warning"
            icon={<Warning sx={{ fontSize: 24 }} />}
            sx={{
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              '& .MuiAlert-message': {
                fontWeight: 500
              }
            }}
          >
            This action will replace the current version with the backup from this deployment.
            Any changes made since then will be permanently lost.
          </Alert>
        </Fade>

        <Typography variant="subtitle2" sx={{
          fontWeight: 700,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <ArrowBack sx={{ fontSize: 18 }} />
          Rolling Back To
        </Typography>

        <Box sx={{
          p: 3,
          borderRadius: 3,
          background: alpha(theme.palette.action.hover, 0.5),
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          mb: 3
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Target */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: alpha(theme.palette.primary.main, 0.1)
              }}>
                <Folder sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Target
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {deployment.target}
                </Typography>
              </Box>
              <Chip
                label={`#${deployment.id}`}
                size="small"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  background: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main
                }}
              />
            </Box>

            {/* Time */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: alpha(theme.palette.info.main, 0.1)
              }}>
                <AccessTime sx={{ color: theme.palette.info.main, fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Deployed At
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatTime(deployment.started_at)}
                </Typography>
              </Box>
            </Box>

            {/* User */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: alpha(theme.palette.success.main, 0.1)
              }}>
                <Person sx={{ color: theme.palette.success.main, fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Deployed By
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {deployment.deployed_by}
                </Typography>
              </Box>
            </Box>

            {/* Commit */}
            {deployment.commit_hash && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: alpha(theme.palette.secondary.main, 0.1)
                }}>
                  <Code sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Commit
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {deployment.commit_hash.substring(0, 12)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{
          p: 2,
          borderRadius: 2,
          background: alpha(theme.palette.info.main, 0.05),
          border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
        }}>
          A new rollback deployment record will be created. The current deployment
          will be replaced with the backup stored at the time of this deployment.
        </Typography>
      </DialogContent>

      <DialogActions sx={{
        p: 3,
        pt: 0,
        gap: 1.5
      }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            borderRadius: 2
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Restore />}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1.2,
            borderRadius: 2,
            boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}`,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.main} 100%)`,
              boxShadow: `0 6px 16px ${alpha(theme.palette.warning.main, 0.4)}`
            },
            '&:disabled': {
              background: 'linear-gradient(135deg, #bdc3c7 0%, #95a5a6 100%)'
            }
          }}
        >
          {loading ? 'Rolling Back...' : 'Confirm Rollback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
